import React, { useCallback, useEffect, useState } from 'react';
import { StepProps } from '../MultiStepModal';
import { jobsApi } from '../../../utils/api';

interface EngineerSuggestion {
  id: string;
  name: string;
  email: string;
  isAvailable: boolean;
  jobsToday: number;
  efficiencyScore: number;
  reason: string;
}

export default function JobScheduleStep({ data, onDataChange }: StepProps) {
  const [suggestions, setSuggestions] = useState<EngineerSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'auto' | 'manual'>(data.engineerId ? 'manual' : 'auto');

  const fetchSuggestions = useCallback(async () => {
    if (!data.scheduledDate || !data.scheduledTime) return;
    try {
      setLoading(true);
      const iso = new Date(`${data.scheduledDate}T${data.scheduledTime}`).toISOString();
      const res = await jobsApi.suggestEngineers(iso, data.estimatedDuration || 120);
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setSuggestions(list);
      // Auto-select top available engineer
      if (mode === 'auto' && list.length > 0 && list[0].isAvailable) {
        onDataChange({ engineerId: list[0].id, engineerName: list[0].name });
      }
    } catch { setSuggestions([]); } finally { setLoading(false); }
  }, [data.scheduledDate, data.scheduledTime, data.estimatedDuration, mode]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const scoreBar = (score: number) => {
    const pct = Math.round((score / 5) * 100);
    return (
      <div className="flex items-center gap-2">
        <div className="h-1.5 w-16 rounded-full bg-[#E5E7EB] overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] text-[#6B7280]">{score}/5</span>
      </div>
    );
  };

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Schedule & assign</h3>
        <p className="step-form-subtitle">Pick a date and time, then let the system suggest the best engineer.</p>
      </div>

      <div className="space-y-4">
        {/* Date + time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-[#374151] mb-1">Date *</label>
            <input
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={data.scheduledDate || ''}
              onChange={e => onDataChange({ scheduledDate: e.target.value })}
              className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#374151] mb-1">Time *</label>
            <input
              type="time"
              value={data.scheduledTime || ''}
              onChange={e => onDataChange({ scheduledTime: e.target.value })}
              className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]"
            />
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-semibold text-[#374151]">Engineer</span>
          <div className="flex rounded-lg border border-[#E6E6E4] overflow-hidden">
            {(['auto', 'manual'] as const).map(m => (
              <button key={m} type="button"
                onClick={() => setMode(m)}
                className={`px-3 py-1 text-[11px] font-medium transition-colors ${mode === m ? 'bg-[#1B1B1B] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                {m === 'auto' ? '⚡ Auto-suggest' : 'Manual pick'}
              </button>
            ))}
          </div>
        </div>

        {/* Suggestions / ranking */}
        {!data.scheduledDate || !data.scheduledTime ? (
          <p className="text-[12px] text-[#9CA3AF] italic">Set date and time to see engineer availability.</p>
        ) : loading ? (
          <div className="space-y-2">
            {[1,2,3].map(i => <div key={i} className="h-14 rounded-xl bg-[#F3F4F6] animate-pulse" />)}
          </div>
        ) : suggestions.length === 0 ? (
          <p className="text-[12px] text-[#9CA3AF]">No engineers found for this company.</p>
        ) : (
          <div className="space-y-2">
            {suggestions.map((eng, idx) => {
              const selected = data.engineerId === eng.id;
              return (
                <button key={eng.id} type="button"
                  onClick={() => onDataChange({ engineerId: eng.id, engineerName: eng.name })}
                  className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all ${
                    selected ? 'border-[#1B1B1B] bg-[#F8F8F8] ring-1 ring-[#1B1B1B]' :
                    !eng.isAvailable ? 'border-[#FCA5A5] bg-[#FEF2F2] opacity-70' :
                    'border-[#E6E6E4] bg-white hover:border-[#CDCDCD] hover:bg-[#FAFAFA]'
                  }`}>
                  {/* Rank badge */}
                  {idx === 0 && eng.isAvailable && mode === 'auto' && (
                    <span className="flex-shrink-0 rounded-full bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 uppercase tracking-wide">Best</span>
                  )}
                  <div className="h-8 w-8 rounded-full bg-[#E5E7EB] flex items-center justify-center text-[12px] font-semibold text-[#374151] flex-shrink-0">
                    {eng.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-[#191919]">{eng.name}</p>
                    <p className="text-[11px] text-[#6B7280]">{eng.reason}</p>
                    {eng.isAvailable && scoreBar(eng.efficiencyScore)}
                  </div>
                  {!eng.isAvailable && (
                    <span className="flex-shrink-0 text-[10px] font-medium text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Conflict</span>
                  )}
                  {selected && (
                    <span className="flex-shrink-0 h-4 w-4 rounded-full bg-[#1B1B1B] flex items-center justify-center">
                      <svg className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 12 12" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
